export default {
  data () {
    return {
      focus: {
        element: null,
        items: [],
        initialFocusItem: null,
        firstItem: null,
        lastItem: null,
        escapeCallback: () => {},
        tabCallback: () => {},
        tabbingEventListenersAdded: false
      }
    }
  },
  computed: {
    leaveFocusOnTab () {
      return !!this.focus.tabCallback
    },
    checkKeysFirstLast () {
      return this.leaveFocusOnTab ? ['up', 'down'] : ['Tab']
    }
  },
  methods: {
    /**
     * Activates focus trap inside a given element.
     * @param {HTMLElement} element - Container element to trap focus inside
     * @param {string} focusableElements - CSS selector for focusable elements (e.g. 'a, button, input')
     * @param {number} initialFocusIndex - Index of element to receive initial focus
     * @param {Function} escapeCallback - Called when Escape key is pressed
     */
    keepTabbingFocus (element, focusableElements, initialFocusIndex = 0, escapeCallback, tabCallback = null) {
      // Only activate focus trap if user is using keyboard
      if (document.body.classList.contains('bx-js-user-is-tabbing')) {
        this.focus.escapeCallback = escapeCallback
        this.focus.tabCallback = tabCallback || null
        this.focus.element = element
        this.focus.items = []

        // Find all focusable elements inside the container
        const nodeList = Array.from(element.querySelectorAll(focusableElements))
        this.focus.items.push(...nodeList)

        // Assign initial, first, and last elements
        this.focus.initialFocusItem = this.focus.items[initialFocusIndex] || this.focus.items[0]
        this.focus.firstItem = this.focus.items[0]
        this.focus.lastItem = this.focus.items.at(-1)

        // Focus the initial element after a short delay
        setTimeout(() => {
          this.focus.initialFocusItem?.focus()
        }, 100)

        this.addTabbingEventListeners()
      }
    },

    /**
     * Adds event listeners to loop tabbing and handle Escape key
     */
    addTabbingEventListeners () {
      this.focus.element.addEventListener('keyup', this.eventKeyupEscape)
      if (this.leaveFocusOnTab) {
        this.focus.element.addEventListener('keydown', this.eventKeydownTab)
        this.focus.element.addEventListener('keydown', this.eventKeydownArrow)
      } else {
        this.focus.firstItem.addEventListener('keydown', this.eventKeydownFirst)
        this.focus.lastItem.addEventListener('keydown', this.eventKeydownLast)
      }
      this.focus.tabbingEventListenersAdded = true
    },

    /**
     * Removes event listeners to clean up
     */
    removeTabbingEventListeners () {
      if (this.focus.tabbingEventListenersAdded) {
        this.focus.element.removeEventListener('keyup', this.eventKeyupEscape)
        if (this.leaveFocusOnTab) {
          this.focus.element.removeEventListener('keydown', this.eventKeydownTab)
          this.focus.element.removeEventListener('keydown', this.eventKeydownArrow)
        } else {
          this.focus.firstItem.removeEventListener('keydown', this.eventKeydownFirst)
          this.focus.lastItem.removeEventListener('keydown', this.eventKeydownLast)
        }
        this.focus.tabbingEventListenersAdded = false
      }
    },

    /**
     * Handles Shift+Tab on the first focusable item
     * Loops focus to the last item
     */
    eventKeydownFirst (e) {
      if (this.checkKeysFirstLast.includes(e.key) && e.shiftKey) {
        e.preventDefault()
        this.focus.lastItem?.focus()
      }
    },

    /**
     * Handles Tab on the last focusable item
     * Loops focus back to the first item
     */
    eventKeydownLast (e) {
      if (this.checkKeysFirstLast.includes(e.key) && !e.shiftKey) {
        e.preventDefault()
        this.focus.firstItem?.focus()
      }
    },

    /**
     * Handles Escape key to trigger callback
     */
    eventKeyupEscape (e) {
      if (e.key === 'Escape') {
        this.focus.escapeCallback?.()
      }
    },
    eventKeydownTab (e) {
      if (e.type === 'keydown' && e.key === 'Tab') {
        e.preventDefault()
        this.focus.tabCallback()
      }
    },
    eventKeydownArrow (e) {
      const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
      if (!keys.includes(e.key)) return

      const visibleItems = this.focus.items.filter(el => el.offsetParent !== null)
      const currentIndex = visibleItems.indexOf(document.activeElement)

      if (e.key === 'ArrowDown' && currentIndex < visibleItems.length - 1) {
        e.preventDefault()
        visibleItems[currentIndex + 1].focus()
      }

      if (e.key === 'ArrowUp' && currentIndex > 0) {
        e.preventDefault()
        visibleItems[currentIndex - 1].focus()
      }

      if (e.key === 'Home') {
        e.preventDefault()
        visibleItems[0]?.focus()
      }

      if (e.key === 'End') {
        e.preventDefault()
        visibleItems[visibleItems.length - 1]?.focus()
      }
    },
    getNextFocusableElement (el) {
      const focusableSelector = `
        a[href],
        area[href],
        input:not([disabled]):not([tabindex="-1"]),
        select:not([disabled]),
        textarea:not([disabled]),
        button:not([disabled]),
        iframe,
        object,
        embed,
        [tabindex]:not([tabindex="-1"]),
        [contenteditable]
      `

      const all = Array.from(document.querySelectorAll(focusableSelector))
        .filter(el =>
          el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
        )

      const currentIndex = all.indexOf(el)
      return all[currentIndex + 1] || null
    }
  }
}
